/**
 * Live / Backtested / Full Journey: a strategy with a "live since" date
 * (Strategies → Live data) shows data before that day as backtested and from
 * it on as live. Without a date there's no split and none of this applies.
 */
import { weekDayDateKeyFromMonday } from './strategySheetMetrics';

export const JOURNEY_VIEWS = [
  { id: 'live', label: 'Live' },
  { id: 'backtested', label: 'Backtested' },
  { id: 'full', label: 'Full Journey' },
];

/** 'YYYY-MM-DD' or null. */
export function normalizeLiveSince(value) {
  const s = String(value || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

/** Whether a day (YYYY-MM-DD) belongs to the view. */
export function dayInJourney(dateKey, liveSince, view) {
  if (!liveSince || !dateKey || view === 'full') return true;
  return view === 'live' ? dateKey >= liveSince : dateKey < liveSince;
}

/**
 * The weekly strategy grid ([Monday mm.dd.yyyy, …, Mon…Fri cells]) limited to
 * the view: days outside it are blanked (a week that straddles the live date
 * keeps only its side), weeks with nothing left are dropped.
 */
export function filterSheetRowsByJourney(rows, liveSince, view) {
  if (!liveSince || view === 'full' || !Array.isArray(rows) || rows.length <= 2) return rows;
  const header = rows[1] || [];
  const dayCols = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
    .map((d, i) => {
      const idx = header.findIndex((c) => String(c || '').toLowerCase() === d);
      return idx >= 0 ? idx : i + 1;
    });
  const out = [rows[0], rows[1]];
  rows.slice(2).forEach((row) => {
    if (!Array.isArray(row)) return;
    const keys = dayCols.map((_, i) => weekDayDateKeyFromMonday(row[0], i));
    if (keys.every((k) => !k)) { out.push(row); return; } // not a dated week: leave it
    const inView = keys.map((k) => dayInJourney(k, liveSince, view));
    if (!inView.some(Boolean)) return;
    if (inView.every(Boolean)) { out.push(row); return; }
    const copy = [...row];
    dayCols.forEach((col, i) => { if (!inView[i]) copy[col] = ''; });
    out.push(copy);
  });
  return out;
}

/** Points ({ date: 'YYYY-MM-DD', … }) limited to the view. */
export function filterPointsByJourney(points, liveSince, view) {
  if (!liveSince || view === 'full' || !Array.isArray(points)) return points;
  return points.filter((p) => dayInJourney(String(p?.date || '').slice(0, 10), liveSince, view));
}

/** The day before 'YYYY-MM-DD' (the last backtested day). */
export function dayBeforeIso(iso) {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}
